import {
  differenceInCalendarDays,
  endOfDay,
  format,
  isSameDay,
  startOfDay,
  subDays,
} from "date-fns";
import { useId, useMemo, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { Button } from "@/app/components/ui/button";
import { DateSpinner } from "@/app/components/ui/date-spinner";
import { Label } from "@/app/components/ui/label";
import { type RoleFilter, useDagStore } from "@/app/hooks/useDagStore";
import { calculateRoleCounts } from "@/app/utils/roleFilters";

export interface SlackFilters {
  startDate?: Date;
  endDate?: Date;
  teamMemberType: "all" | "teamMembers" | "nonTeamMembers";
  roleFilter: RoleFilter;
}

interface SlackNewsFiltersProps {
  filters: SlackFilters;
  onFiltersChange: (filters: SlackFilters) => void;
  className?: string;
}

export function SlackNewsFilters({
  filters,
  onFiltersChange,
  className = "",
}: SlackNewsFiltersProps) {
  const startDateId = useId();
  const endDateId = useId();
  const teamMembers = useDagStore(useShallow((state) => state.teamMembersFiltered));
  const roleCounts = useMemo(() => calculateRoleCounts(teamMembers), [teamMembers]);

  // Define the range: allow selecting from the beginning of Slack history to today
  const today = useMemo(() => new Date(), []);
  const earliestPossibleDate = useMemo(() => startOfDay(new Date("2025-09-01")), []); // Team-Dash Slack news integration started in Oct 2025
  const startOfToday = useMemo(() => startOfDay(today), [today]);
  const endOfToday = useMemo(() => endOfDay(today), [today]);

  // Default to showing past two days when no filter is set
  const defaultStartDate = startOfDay(subDays(today, 2));

  // Convert dates to ISO format for DateSpinner
  const toISODate = (date: Date) => format(date, "yyyy-MM-dd");
  const fromISODate = (isoString: string) => new Date(`${isoString}T00:00:00.000Z`);

  // Track input values separately from filter values
  const [startDateInput, setStartDateInput] = useState(
    filters.startDate ? toISODate(filters.startDate) : toISODate(defaultStartDate),
  );
  const [endDateInput, setEndDateInput] = useState(
    filters.endDate ? toISODate(filters.endDate) : toISODate(today),
  );

  // Helper to validate date strings
  const isValidDateString = (dateString: string) => {
    if (!dateString) return true; // Empty is valid
    if (dateString.length !== 10) return false;
    if (!dateString.match(/^\d{4}-\d{2}-\d{2}$/)) return false;

    try {
      const [year, month, day] = dateString.split("-").map(Number) as [number, number, number];
      const date = new Date(year, month - 1, day);
      return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
    } catch {
      return false;
    }
  };

  const handleStartDateChange = (value: string) => {
    setStartDateInput(value);

    const trimmedValue = value.trim();
    if (trimmedValue === "") {
      onFiltersChange({
        ...filters,
        startDate: undefined,
      });
    } else if (isValidDateString(trimmedValue)) {
      try {
        const date = fromISODate(trimmedValue);
        onFiltersChange({
          ...filters,
          startDate: startOfDay(date),
        });
      } catch {
        // Don't update filter for invalid dates
      }
    }
  };

  const handleEndDateChange = (value: string) => {
    setEndDateInput(value);

    const trimmedValue = value.trim();
    if (trimmedValue === "") {
      onFiltersChange({
        ...filters,
        endDate: undefined,
      });
    } else if (isValidDateString(trimmedValue)) {
      try {
        const date = fromISODate(trimmedValue);
        onFiltersChange({
          ...filters,
          endDate: endOfDay(date),
        });
      } catch {
        // Don't update filter for invalid dates
      }
    }
  };

  const currentRange = useMemo(() => {
    if (!filters.startDate || !filters.endDate) {
      return null;
    }

    const normalizedStart = startOfDay(filters.startDate);
    const normalizedEnd = startOfDay(filters.endDate);

    return {
      start: normalizedStart,
      end: normalizedEnd,
      diff: differenceInCalendarDays(normalizedEnd, normalizedStart),
    };
  }, [filters.startDate, filters.endDate]);

  const applyRangePreset = (targetDiff: number) => {
    const endDate = endOfToday;
    let startDate = targetDiff === 0 ? startOfToday : startOfDay(subDays(endDate, targetDiff));

    if (startDate < earliestPossibleDate) {
      startDate = earliestPossibleDate;
    }

    setStartDateInput(toISODate(startDate));
    setEndDateInput(toISODate(endDate));
    onFiltersChange({
      ...filters,
      startDate,
      endDate,
    });
  };

  const applyAllTimePreset = () => {
    const startDate = earliestPossibleDate;
    const endDate = endOfToday;
    setStartDateInput(toISODate(startDate));
    setEndDateInput(toISODate(endDate));
    onFiltersChange({
      ...filters,
      startDate,
      endDate,
    });
  };

  const handleRoleFilterChange = (role: RoleFilter) => {
    onFiltersChange({
      ...filters,
      roleFilter: role,
    });
  };

  const isAllTimeActive = currentRange
    ? isSameDay(currentRange.start, earliestPossibleDate) &&
      isSameDay(currentRange.end, startOfToday)
    : false;

  const isRangePresetActive = (targetDiff: number) => {
    if (!currentRange) return false;

    if (targetDiff === 0) {
      return (
        currentRange.diff === 0 &&
        isSameDay(currentRange.start, startOfToday) &&
        isSameDay(currentRange.end, startOfToday)
      );
    }

    return currentRange.diff === targetDiff;
  };

  return (
    <div className={`space-y-4 ${className} px-2`}>
      {/* Date Range Filter */}
      <div className="space-y-3">
        <Label className="text-sm font-semibold text-stone-700">Date Range</Label>
        <div className="space-y-3">
          <div className="flex gap-2 items-center">
            <div className="flex-1">
              <Label htmlFor={startDateId} className="text-xs text-stone-600">
                From
              </Label>
              <DateSpinner
                id={startDateId}
                value={startDateInput}
                onChange={handleStartDateChange}
                placeholder="2025-10-10"
              />
            </div>
            <span className="text-stone-400 pt-5">—</span>
            <div className="flex-1">
              <Label htmlFor={endDateId} className="text-xs text-stone-600">
                To
              </Label>
              <DateSpinner
                id={endDateId}
                value={endDateInput}
                onChange={handleEndDateChange}
                placeholder="2025-10-13"
              />
            </div>
          </div>
          {/* Quick presets */}
          <div className="flex gap-1 flex-wrap">
            <Button
              variant={isRangePresetActive(0) ? "secondary" : "ghost"}
              size="sm"
              onClick={() => applyRangePreset(0)}
              className="text-xs px-2 py-1 h-auto"
            >
              Today
            </Button>
            <Button
              variant={isRangePresetActive(2) ? "secondary" : "ghost"}
              size="sm"
              onClick={() => applyRangePreset(2)}
              className="text-xs px-2 py-1 h-auto"
            >
              2 days
            </Button>
            <Button
              variant={isRangePresetActive(7) ? "secondary" : "ghost"}
              size="sm"
              onClick={() => applyRangePreset(7)}
              className="text-xs px-2 py-1 h-auto"
            >
              7 days
            </Button>
            <Button
              variant={isRangePresetActive(14) ? "secondary" : "ghost"}
              size="sm"
              onClick={() => applyRangePreset(14)}
              className="text-xs px-2 py-1 h-auto"
            >
              2 weeks
            </Button>
            <Button
              variant={isRangePresetActive(30) ? "secondary" : "ghost"}
              size="sm"
              onClick={() => applyRangePreset(30)}
              className="text-xs px-2 py-1 h-auto"
            >
              30 days
            </Button>
            <Button
              variant={isRangePresetActive(90) ? "secondary" : "ghost"}
              size="sm"
              onClick={() => applyRangePreset(90)}
              className="text-xs px-2 py-1 h-auto"
            >
              3 months
            </Button>
            <Button
              variant={isRangePresetActive(365) ? "secondary" : "ghost"}
              size="sm"
              onClick={() => applyRangePreset(365)}
              className="text-xs px-2 py-1 h-auto"
            >
              1 year
            </Button>
            <Button
              variant={isAllTimeActive ? "secondary" : "ghost"}
              size="sm"
              onClick={applyAllTimePreset}
              className="text-xs px-2 py-1 h-auto"
            >
              All time
            </Button>
          </div>
        </div>
      </div>

      {/* Role Filter */}
      <div className="space-y-2">
        <Label className="text-sm font-semibold text-stone-700">Role Filter</Label>
        <div className="space-y-2">
          {[
            { value: "All", label: "All" },
            { value: "Core Team", label: "Core Team" },
            { value: "Core Team - FT", label: "Core Team - FT" },
            { value: "Contractors", label: "Contractors" },
            { value: "Other", label: "Other" },
          ].map((option) => (
            <label key={option.value} className="flex items-center">
              <input
                type="radio"
                name="roleFilter"
                checked={filters.roleFilter === option.value}
                onChange={() => handleRoleFilterChange(option.value as RoleFilter)}
                className="h-4 w-4 text-blue-600 border-stone-300 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-stone-700">
                {option.label} ({roleCounts[option.value as RoleFilter]})
              </span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}
